import { useContext, useCallback, useMemo } from 'react';
import { BeerContext } from '../BeerContext';
import { 
  FetchBeersStartAction,
  FetchBeersSuccessAction,
  FetchBeersFailureAction,
  UpdateBeerPropertyAction
} from '../actions/beerDataActions';
import { Beer } from '../types';

/**
 * Hook for accessing and manipulating beer data
 * Following Interface Segregation Principle by providing a focused API
 */
export function useBeerData() {
  const context = useContext(BeerContext);
  
  if (context === null) {
    throw new Error('useBeerData must be used within a BeerProvider');
  }
  
  const { state, dispatch } = context;
  
  // Beer data state selectors
  const beers = state.beers;
  const loading = state.loading;
  const error = state.error;
  
  // State is now managed without debug logging
  
  // Filtered beers selector
  const filteredBeers = useMemo(() => {
    const { filters, userInteractions } = state;
    
    return beers.filter(beer => {
      // Search term filter
      if (filters.searchTerm && !beer.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) && 
          !beer.brewery.toLowerCase().includes(filters.searchTerm.toLowerCase())) {
        return false;
      }
      
      // Type filter
      if (filters.typeFilter && beer.style !== filters.typeFilter) {
        return false;
      }
      
      // Brewery filter
      if (filters.breweryFilter && beer.brewery !== filters.breweryFilter) {
        return false;
      }
      
      // Drink type filter
      if (filters.drinkTypeFilter && beer.type !== filters.drinkTypeFilter) {
        return false;
      }
      
      // Availability filter
      if (filters.availabilityFilter && beer.availability !== filters.availabilityFilter) {
        return false;
      }
      
      // Tag filters
      if (filters.tagFilters.length > 0) {
        const hasAllTags = filters.tagFilters.every(tag => 
          beer.tags && beer.tags.includes(tag)
        );
        
        if (!hasAllTags) {
          return false;
        }
      }
      
      // Show only favorites filter
      if (filters.showOnlyFavorites && !userInteractions.favorites.includes(beer.id)) {
        return false;
      }
      
      // Show only available filter
      if (filters.showOnlyAvailable && beer.availability !== 'available') {
        return false;
      }
      
      return true;
    });
  }, [beers, state]);
  
  // Beer data action creators
  const fetchBeers = useCallback(async () => {
    dispatch(new FetchBeersStartAction());
    
    try {
      // Simulate API call - replace with actual API call
      const response = await fetch('/api/beers');
      const data = await response.json();
      
      dispatch(new FetchBeersSuccessAction(data));
    } catch (error) {
      dispatch(new FetchBeersFailureAction(error instanceof Error ? error : new Error('Unknown error')));
    }
  }, [dispatch]);
  
  const updateBeerProperty = useCallback(<K extends keyof Beer>(beerId: string, property: K, value: Beer[K]) => {
    dispatch(new UpdateBeerPropertyAction({ beerId, property, value }));
  }, [dispatch]);
  
  // Beer data selectors
  const getBeerById = useCallback((id: string) => {
    return beers.find(beer => beer.id === id);
  }, [beers]);
  
  return {
    // State
    beers,
    loading,
    error,
    filteredBeers,
    
    // Actions
    fetchBeers,
    updateBeerProperty,
    
    // Selectors
    getBeerById,
  };
}
